import { apiClient } from './api';

/**
 * Legal & Compliance Analytics service (VIS-151).
 *
 * Like {@link ./legal-dashboard.service}, this is frontend-orchestrated
 * aggregation over EXISTING `/api/v1/legal/*` endpoints — no backend, no
 * Corporate Security data. It produces metric cards, status breakdowns, a
 * time-series trend (HIPAA incident volume, whose granularity follows the
 * time-range filter), and SLA / overdue indicators. A failed source degrades
 * gracefully and is reported in `failedSources`.
 */

/** Time-range options. Each maps to a HIPAA volume granularity. */
export type LegalAnalyticsRange = 'MONTH' | 'QUARTER' | 'YEAR';

export const LEGAL_ANALYTICS_RANGE_OPTIONS: Array<{ value: LegalAnalyticsRange; label: string }> = [
  { value: 'MONTH', label: 'Monthly (last 12 months)' },
  { value: 'QUARTER', label: 'Quarterly' },
  { value: 'YEAR', label: 'Yearly' },
];

/** Endpoints this service may call. Guarded by a unit test (L&C-only). */
export const LC_ANALYTICS_ENDPOINTS = {
  vendorAnalytics: '/api/v1/legal/reporting/analytics',
  mattersSummary: '/api/v1/legal/matters/reporting/summary',
  mattersUpcoming: '/api/v1/legal/matters/reporting/upcoming-dates?days=30',
  trackingDashboard: '/api/v1/legal/tracking/dashboard',
  /** `:granularity` is replaced with the selected range. */
  hipaaDashboard: '/api/v1/legal/hipaa/dashboard?volumeGranularity=:granularity',
} as const;

export type LegalAnalyticsSource = keyof typeof LC_ANALYTICS_ENDPOINTS;

export interface LegalAnalyticsMetric {
  key: string;
  label: string;
  value: number;
  /** Currency metrics render with a `$`; counts render plain. */
  format?: 'number' | 'currency';
}

export interface StatusBreakdown {
  key: string;
  title: string;
  data: Record<string, number>;
}

export interface TrendPoint {
  bucket: string;
  count: number;
}

export type SlaStatus = 'good' | 'warning' | 'critical';

export interface SlaIndicator {
  key: string;
  label: string;
  value: number;
  status: SlaStatus;
}

export interface LegalAnalyticsData {
  range: LegalAnalyticsRange;
  metrics: LegalAnalyticsMetric[];
  breakdowns: StatusBreakdown[];
  trend: { granularity: LegalAnalyticsRange; points: TrendPoint[] };
  sla: SlaIndicator[];
  failedSources: LegalAnalyticsSource[];
}

// ── Minimal response shapes ────────────────────────────────────────────────

interface VendorAnalyticsShape {
  vendors?: { total?: number; byStatus?: Record<string, number>; byRiskTier?: Record<string, number> };
  contracts?: {
    total?: number;
    byStatus?: Record<string, number>;
    totalValue?: number;
    expiringIn30Days?: number;
    expiringIn90Days?: number;
  };
  obligations?: { overdueCount?: number };
}

interface MattersSummaryShape {
  byStatus?: Array<{ status: string; count: number }>;
}

type MattersUpcomingShape = Array<{ id: string }>;

interface TrackingDashboardShape {
  openCount?: number;
  overdueCount?: number;
}

interface HipaaDashboardShape {
  widgets?: {
    openIncidents?: { total?: number; byStatus?: Array<{ status: string; count: number }> };
    notificationDeadlines?: { tierCounts?: Partial<Record<string, number>> };
    volumeMetrics?: {
      granularity?: LegalAnalyticsRange;
      byBucket?: Array<{ bucket: string; count: number }>;
    };
  };
}

function toNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** Convert an array of `{key,count}` rows into a `Record<label, count>`. */
function toRecord<T extends Record<string, unknown>>(
  rows: T[] | undefined,
  keyField: keyof T
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of rows ?? []) {
    const key = String(row[keyField] ?? 'UNKNOWN');
    out[key] = toNumber(row.count);
  }
  return out;
}

class LegalAnalyticsService {
  private async safeGet<T>(
    url: string,
    key: LegalAnalyticsSource,
    failed: LegalAnalyticsSource[]
  ): Promise<T | null> {
    try {
      const res = await apiClient.get<T>(url);
      if (!res.success || res.data === undefined) {
        failed.push(key);
        return null;
      }
      return res.data;
    } catch {
      failed.push(key);
      return null;
    }
  }

  async getAnalytics(range: LegalAnalyticsRange = 'MONTH'): Promise<LegalAnalyticsData> {
    const failedSources: LegalAnalyticsSource[] = [];
    const hipaaUrl = LC_ANALYTICS_ENDPOINTS.hipaaDashboard.replace(':granularity', range);

    const [va, mattersSummary, mattersUpcoming, tracking, hipaa] = await Promise.all([
      this.safeGet<VendorAnalyticsShape>(
        LC_ANALYTICS_ENDPOINTS.vendorAnalytics,
        'vendorAnalytics',
        failedSources
      ),
      this.safeGet<MattersSummaryShape>(
        LC_ANALYTICS_ENDPOINTS.mattersSummary,
        'mattersSummary',
        failedSources
      ),
      this.safeGet<MattersUpcomingShape>(
        LC_ANALYTICS_ENDPOINTS.mattersUpcoming,
        'mattersUpcoming',
        failedSources
      ),
      this.safeGet<TrackingDashboardShape>(
        LC_ANALYTICS_ENDPOINTS.trackingDashboard,
        'trackingDashboard',
        failedSources
      ),
      this.safeGet<HipaaDashboardShape>(hipaaUrl, 'hipaaDashboard', failedSources),
    ]);

    const mattersByStatus = mattersSummary?.byStatus ?? [];
    const openMatters = mattersByStatus.reduce((sum, s) => sum + toNumber(s.count), 0);
    const upcomingCount = Array.isArray(mattersUpcoming) ? mattersUpcoming.length : 0;
    const hipaaWidgets = hipaa?.widgets;

    const metrics: LegalAnalyticsMetric[] = [
      { key: 'vendors', label: 'Total Vendors', value: toNumber(va?.vendors?.total) },
      { key: 'contracts', label: 'Total Contracts', value: toNumber(va?.contracts?.total) },
      {
        key: 'contract-value',
        label: 'Total Contract Value',
        value: toNumber(va?.contracts?.totalValue),
        format: 'currency',
      },
      { key: 'open-matters', label: 'Open Legal Matters', value: openMatters },
      {
        key: 'hipaa-open',
        label: 'Open HIPAA Incidents',
        value: toNumber(hipaaWidgets?.openIncidents?.total),
      },
      {
        key: 'tracking-open',
        label: 'Open Tracking Requests',
        value: toNumber(tracking?.openCount),
      },
    ];

    const breakdowns: StatusBreakdown[] = [
      { key: 'matters-status', title: 'Legal Matters by Status', data: toRecord(mattersByStatus, 'status') },
      { key: 'vendors-status', title: 'Vendors by Status', data: va?.vendors?.byStatus ?? {} },
      { key: 'vendors-risk', title: 'Vendors by Risk Tier', data: va?.vendors?.byRiskTier ?? {} },
      { key: 'contracts-status', title: 'Contracts by Status', data: va?.contracts?.byStatus ?? {} },
      {
        key: 'hipaa-status',
        title: 'HIPAA Incidents by Status',
        data: toRecord(hipaaWidgets?.openIncidents?.byStatus, 'status'),
      },
    ];

    const trendGranularity = hipaaWidgets?.volumeMetrics?.granularity ?? range;
    const trend = {
      granularity: trendGranularity,
      points: (hipaaWidgets?.volumeMetrics?.byBucket ?? []).map((b) => ({
        bucket: b.bucket,
        count: toNumber(b.count),
      })),
    };

    const tierCounts = hipaaWidgets?.notificationDeadlines?.tierCounts ?? {};
    const overdueObligations = toNumber(va?.obligations?.overdueCount);
    const overdueTracking = toNumber(tracking?.overdueCount);
    const hipaaDueSoon = toNumber(tierCounts.RED) + toNumber(tierCounts.AMBER);

    const sla: SlaIndicator[] = [
      {
        key: 'overdue-obligations',
        label: 'Overdue Contract Obligations',
        value: overdueObligations,
        status: countStatus(overdueObligations),
      },
      {
        key: 'hipaa-deadlines',
        label: 'HIPAA Notifications Due Soon / Overdue',
        value: hipaaDueSoon,
        status: countStatus(hipaaDueSoon),
      },
      {
        key: 'overdue-tracking',
        label: 'Overdue Tracking Requests',
        value: overdueTracking,
        status: countStatus(overdueTracking),
      },
      {
        key: 'expiring-contracts',
        label: 'Contracts Expiring (30 days)',
        value: toNumber(va?.contracts?.expiringIn30Days),
        status: countStatus(toNumber(va?.contracts?.expiringIn30Days), 5),
      },
      {
        key: 'matter-key-dates',
        label: 'Matters with Key Dates (30 days)',
        value: upcomingCount,
        status: countStatus(upcomingCount, 5),
      },
    ];

    return { range, metrics, breakdowns, trend, sla, failedSources };
  }

  /**
   * Render the analytics view model as a CSV string. Pure (no I/O) so it is
   * trivially testable; the page wraps the result in a Blob download.
   */
  exportCsv(data: LegalAnalyticsData): string {
    const lines: string[] = [];
    const row = (...cells: Array<string | number>) => lines.push(cells.map(csvCell).join(','));

    row('Section', 'Label', 'Value');
    for (const m of data.metrics) {
      row('Metric', m.label, m.value);
    }
    for (const s of data.sla) {
      row('SLA', `${s.label} (${s.status})`, s.value);
    }
    for (const b of data.breakdowns) {
      for (const [key, value] of Object.entries(b.data)) {
        row(b.title, key, value);
      }
    }
    for (const p of data.trend.points) {
      row(`Trend (${data.trend.granularity})`, p.bucket, p.count);
    }
    return lines.join('\n');
  }
}

/**
 * Higher count = worse. `0` is good; `>= criticalAt` is critical; anything in
 * between is a warning. For "overdue" metrics `criticalAt` defaults to 1 (any
 * overdue item is critical); "due soon" metrics pass a higher threshold.
 */
function countStatus(value: number, criticalAt = 1): SlaStatus {
  if (value <= 0) {return 'good';}
  if (value >= criticalAt) {return 'critical';}
  return 'warning';
}

/** Escape a CSV cell per RFC 4180 (quote if it contains `,`, `"`, or newline). */
function csvCell(value: string | number): string {
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export const legalAnalyticsService = new LegalAnalyticsService();
